import React, { useEffect } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import PropTypes from 'prop-types';
import { object, string, date } from 'yup';
import { TextInput, SelectInput, RadioInput, CheckboxInput } from '../Helpers/Inputs';
import { InputMaskDate, NumberFormatDollars } from '../Helpers/Formatters';
import { IssuedHealthClasses, PaymentOptions } from './SelectOptions';
import {customValidators} from "../Helpers/Validators/customValidators";
import BackNextButton from '../Common/BackNextButton';
import TitleComponent from '../Common/TitleComponent';

export default function ExistingPolicyInformation(props) {
    const ledger = props.fullFormState.pages.inforceLedger && props.fullFormState.pages.inforceLedger.inForceLedgerType
     const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
        validationSchema: ExistingPolicyInformation.ValidationSchema(props.isTerm, ledger),
        mode: 'onBlur',
        reValidateMode: 'onBlur',
    });

    const handleValueChange = (field, value) => {
        if (field === 'otherOwner' && value === false) {
            let fullForm = { ...props.fullFormState };
            props.fullFormState.pages.existingPolicyInformation.otherOwner = value;
            props.fullFormState.pages.existingPolicyInformation.ownerType = '';
            props.fullFormState.pages.existingPolicyInformation.ownerFirstName = '';
            props.fullFormState.pages.existingPolicyInformation.ownerLastName = '';
            props.fullFormState.pages.existingPolicyInformation.ownerEmail = '';
            props.fullFormState.pages.existingPolicyInformation.trustName = '';
            props.fullFormState.pages.existingPolicyInformation.trusteeEmail = '';
            props.fullFormState.pages.existingPolicyInformation.taxID = '';
            setValue(field, value);
            props.onMultiFieldChange(fullForm);
        }
        if (field === 'ownerType' && value === 'person') {
            let fullForm = { ...props.fullFormState };
            props.fullFormState.pages.existingPolicyInformation.ownerType = value;
            props.fullFormState.pages.existingPolicyInformation.trustName = '';
            props.fullFormState.pages.existingPolicyInformation.trusteeEmail = '';
            props.fullFormState.pages.existingPolicyInformation.taxID = '';
            setValue(field, value);
            props.onMultiFieldChange(fullForm);
        }
        if (field === 'ownerType' && value === 'trust') {
            let fullForm = { ...props.fullFormState };
            props.fullFormState.pages.existingPolicyInformation.ownerType = value;
            props.fullFormState.pages.existingPolicyInformation.ownerFirstName = '';
            props.fullFormState.pages.existingPolicyInformation.ownerLastName = '';
            props.fullFormState.pages.existingPolicyInformation.ownerEmail = '';
            setValue(field, value);
            props.onMultiFieldChange(fullForm);
        }
        else {
            setValue(field, value);
            props.onFieldChange(field, value);
        }
        triggerValidation(field);
    };

    useEffect(() => {
        // manually register fields for validation that don't play nice with react-hook-form register
        register({ name: 'carrier' });
        setValue('carrier', props.formState.carrier);

        register({ name: 'planName' });
        setValue('planName', props.formState.planName);

        register({ name: 'policyNumber' });
        setValue('policyNumber', props.formState.policyNumber);

        register({ name: 'health' });
        setValue('health', props.formState.health);

        register({ name: 'premium' });
        setValue('premium', props.formState.premium);

        register({ name: 'paymentFrequency' });
        setValue('paymentFrequency', props.formState.paymentFrequency);

        register({ name: 'deathBenefit' });
        setValue('deathBenefit', props.formState.deathBenefit);

        register({ name: 'policyIssuedDate' });
        setValue('policyIssuedDate', props.formState.policyIssuedDate);

        register({ name: 'tableRating' });
        setValue('tableRating', props.formState.tableRating);

        register({ name: 'riders' });
        setValue('riders', props.formState.riders);

        register({ name: 'tobacco' });
        setValue('tobacco', props.formState.tobacco);

        register({ name: 'otherOwner' });
        setValue('otherOwner', props.formState.otherOwner);

        register({ name: 'coverage' });
        setValue('coverage', props.formState.coverage);

        register({ name: 'objective' });
        setValue('objective', props.formState.objective);

        register({ name: 'ownerFirstName' });
        setValue('ownerFirstName', props.formState.ownerFirstName);

        register({ name: 'ownerLastName' });
        setValue('ownerLastName', props.formState.ownerLastName);

        register({ name: 'ownerEmail' });
        setValue('ownerEmail', props.formState.ownerEmail);

        register({ name: 'trustName' });
        setValue('trustName', props.formState.trustName);

        register({ name: 'trusteeEmail' });
        setValue('trusteeEmail', props.formState.trusteeEmail);

        register({ name: 'taxID' });
        setValue('taxID', props.formState.taxID);

        register({ name: 'ownerType' });
        setValue('ownerType', props.formState.ownerType);

        register({ name: 'beneficiaries' });
        setValue('beneficiaries', props.formState.beneficiaries);

    }, [register]);

    const handleOnSubmit = () => {
        if(props.fullFormState.pages.inforceLedger && props.fullFormState.pages.inforceLedger.inForceLedgerType === 'inforceLedgerNeeded'){
            props.submitRequest();
        }
        else{
            props.onNext();
        }
    }

    return (
        <Container fluid className='border-class'>
            <form onSubmit={handleSubmit(handleOnSubmit)}>
                <Row className="justify-content-center pt-4">
                    <Col lg={4} className="pt-3">
                        <TitleComponent title="Existing Policy Info" />
                        {(props.isTerm || (props.fullFormState.pages.inforceLedger && props.fullFormState.pages.inforceLedger.inForceLedgerType === 'inforceLedgerNeeded')) &&
                            <div>
                                <Form.Group>
                                    <TextInput
                                        name="carrier"
                                        id="carrier"
                                        label="Carrier"
                                        value={props.formState.carrier}
                                        onChange={e => handleValueChange('carrier', e.target.value)}
                                        errors={errors.carrier}
                                        inputRef={register}
                                    />
                                </Form.Group>
                                <Form.Group>
                                    <TextInput
                                        name="policyNumber"
                                        id="policyNumber"
                                        label="Policy Number"
                                        value={props.formState.policyNumber}
                                        onChange={e => handleValueChange('policyNumber', e.target.value)}
                                        errors={errors.policyNumber}
                                        inputRef={register}
                                    />
                                </Form.Group>
                                {props.isTerm &&
                                    <div>
                                        <Form.Group>
                                            <TextInput
                                                name="planName"
                                                id="planName"
                                                label="Plan Name or Type"
                                                value={props.formState.planName}
                                                onChange={e => handleValueChange('planName', e.target.value)}
                                                errors={errors.planName}
                                                inputRef={register}
                                            />
                                        </Form.Group>



                                        <Form.Group>
                                            <SelectInput
                                                name="health"
                                                id="health"
                                                value={props.formState.health}
                                                onChange={(e) => handleValueChange('health', e.target.value)}
                                                label="Issued Health Class"
                                                inputRef={register}
                                                selectOptions={IssuedHealthClasses()}
                                                errors={errors && errors.health}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="premium"
                                                id="premium"
                                                label="Current Premium"
                                                value={props.formState.premium}
                                                onChange={e => handleValueChange('premium', e.target.value)}
                                                errors={errors.premium}
                                                inputRef={register}
                                                InputProps={{
                                                    inputComponent: NumberFormatDollars,
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <SelectInput
                                                name="paymentFrequency"
                                                id="paymentFrequency"
                                                value={props.formState.paymentFrequency}
                                                onChange={(e) => handleValueChange('paymentFrequency', e.target.value)}
                                                label="Payment Frequency"
                                                inputRef={register}
                                                selectOptions={PaymentOptions()}
                                                errors={errors && errors.paymentFrequency}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="deathBenefit"
                                                id="deathBenefit"
                                                label="Death Benefit"
                                                value={props.formState.deathBenefit}
                                                onChange={e => handleValueChange('deathBenefit', e.target.value)}
                                                errors={errors.deathBenefit}
                                                inputRef={register}
                                                InputProps={{
                                                    inputComponent: NumberFormatDollars,
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="policyIssuedDate"
                                                id="policyIssuedDate"
                                                label="Date Policy was Issued"
                                                value={props.formState.policyIssuedDate}
                                                errors={errors && errors.policyIssuedDate}
                                                inputRef={register}
                                                onChange={(e) => handleValueChange('policyIssuedDate', e.target.value)}
                                                InputProps={{
                                                    inputComponent: InputMaskDate,
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="tableRating"
                                                id="tableRating"
                                                label="Table Rating"
                                                value={props.formState.tableRating}
                                                errors={errors && errors.tableRating}
                                                inputRef={register}
                                                onChange={(e) => handleValueChange('tableRating', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                id="riders"
                                                name="riders"
                                                value={props.formState.riders}
                                                label="Riders"
                                                multiline
                                                rows={6}
                                                variant="filled"
                                                onChange={e => handleValueChange('riders', e.target.value)}
                                                errors={errors.riders}
                                                inputRef={register}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <RadioInput
                                                name="tobacco"
                                                id="tobacco"
                                                label="Do you use Tobacco?"
                                                value={props.formState.tobacco}
                                                radios={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                                                onChange={e => handleValueChange('tobacco', e.target.value)}
                                                errors={errors.tobacco}
                                                inputRef={register}
                                            />
                                        </Form.Group>
                                    </div>
                                }
                            </div>
                        }
                        {
                            (!props.isTerm  && (props.fullFormState.pages.inforceLedger && props.fullFormState.pages.inforceLedger.inForceLedgerType !== 'inforceLedgerNeeded')) &&
                            <TextInput
                                name="beneficiaries"
                                id="beneficiaries"
                                label="Beneficiaries"
                                value={props.formState.beneficiaries}
                                onChange={e => handleValueChange('beneficiaries', e.target.value)}
                                errors={errors.beneficiaries}
                                inputRef={register}
                            />
                        }

                        <Form.Group className="m-0">
                            <CheckboxInput
                                name="otherOwner"
                                id="otherOwner"
                                checked={props.formState.otherOwner}
                                onChange={(e) => handleValueChange('otherOwner', e.target.checked)}
                                errors={errors.otherOwner}
                                inputRef={register}
                                label="Is there an owner other than insured?"
                                color="primary"
                                className="us-resident"
                            />
                        </Form.Group>

                        {props.formState.otherOwner &&
                            <React.Fragment>
                                <Form.Group>
                                    <SelectInput
                                        name="ownerType"
                                        id="ownerType"
                                        value={props.formState.ownerType}
                                        onChange={(e) => handleValueChange('ownerType', e.target.value)}
                                        label="Owner Type"
                                        inputRef={register}
                                        selectOptions={[
                                            { label: "Person", value: "person" },
                                            { label: "Trust", value: "trust" }
                                        ]}
                                        errors={errors && errors.ownerType}
                                    />
                                </Form.Group>
                                {props.formState.ownerType === 'person' ?
                                    <React.Fragment>
                                        <Form.Group>
                                            <TextInput
                                                name="ownerFirstName"
                                                id="ownerFirstName"
                                                label="Owner First Name"
                                                value={props.formState.ownerFirstName}
                                                errors={errors && errors.ownerFirstName}
                                                inputRef={register}
                                                onChange={(e) => handleValueChange('ownerFirstName', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="ownerLastName"
                                                id="ownerLastName"
                                                label="Owner Last Name"
                                                value={props.formState.ownerLastName}
                                                errors={errors && errors.ownerLastName}
                                                inputRef={register}
                                                onChange={(e) => handleValueChange('ownerLastName', e.target.value)}
                                            />
                                        </Form.Group>

                                        <Form.Group>
                                            <TextInput
                                                name="ownerEmail"
                                                id="ownerEmail"
                                                label="Owner Email"
                                                value={props.formState.ownerEmail}
                                                errors={errors && errors.ownerEmail}
                                                inputRef={register}
                                                onChange={(e) => handleValueChange('ownerEmail', e.target.value)}
                                            />
                                        </Form.Group>
                                    </React.Fragment>
                                    :
                                    props.formState.ownerType === 'trust' ?

                                        <React.Fragment>
                                            <Form.Group>
                                                <TextInput
                                                    name="trustName"
                                                    id="trustName"
                                                    label="Trust Name"
                                                    value={props.formState.trustName}
                                                    errors={errors && errors.trustName}
                                                    inputRef={register}
                                                    onChange={(e) => handleValueChange('trustName', e.target.value)}
                                                />
                                            </Form.Group>

                                            <Form.Group>
                                                <TextInput
                                                    name="trusteeEmail"
                                                    id="trusteeEmail"
                                                    label="Trustee Email"
                                                    value={props.formState.trusteeEmail}
                                                    errors={errors && errors.trusteeEmail}
                                                    inputRef={register}
                                                    onChange={(e) => handleValueChange('trusteeEmail', e.target.value)}
                                                />
                                            </Form.Group>

                                            <Form.Group>
                                                <TextInput
                                                    name="taxID"
                                                    id="taxID"
                                                    label="Tax ID"
                                                    value={props.formState.taxID}
                                                    errors={errors && errors.taxID}
                                                    inputRef={register}
                                                    onChange={(e) => handleValueChange('taxID', e.target.value)}
                                                />
                                            </Form.Group>
                                        </React.Fragment>
                                        : null
                                }
                            </React.Fragment>
                        }

                        {(props.fullFormState.pages.inforceLedger && props.fullFormState.pages.inforceLedger.inForceLedgerType !== 'inforceLedgerNeeded') &&
                            <div>
                                <Form.Group>
                                    <TextInput
                                        id="objective"
                                        name="objective"
                                        value={props.formState.objective}
                                        label="What is the Objective?"
                                        multiline
                                        rows={6}
                                        variant="filled"
                                        onChange={e => handleValueChange('objective', e.target.value)}
                                        errors={errors.objective}
                                        inputRef={register}
                                    />
                                </Form.Group>

                                <Form.Group>
                                    <TextInput
                                        name="coverage"
                                        id="coverage"
                                        label="Current Coverage Needed"
                                        value={props.formState.coverage}
                                        errors={errors && errors.coverage}
                                        inputRef={register}
                                        onChange={(e) => handleValueChange('coverage', e.target.value)}
                                        InputProps={{
                                            inputComponent: NumberFormatDollars,
                                        }}
                                    />
                                </Form.Group>
                            </div>
                        }
                        {
                             props.error!=="" &&
                            <Row className='justify-content-center mb-3 px-5'>
                                <div className='errorMessageCustom'>{props.error}</div>
                            </Row>
                        }
                    </Col>
                </Row>
                <BackNextButton onBack={props.onBack} continueLabel={ledger === "inforceLedgerNeeded" ? 'SUBMIT' : 'NEXT'} isLoading={props.loading} disabled={props.loading} />
            </form>
        </Container >
    )
}

ExistingPolicyInformation.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    onFieldChange: PropTypes.func.isRequired,
};

ExistingPolicyInformation.ValidationSchema = (isTerm,ledger) => {
    const common = {
        carrier: isTerm || ledger === "inforceLedgerNeeded" ? customValidators.string()
        .isValidNameLimitedSpecialChar("Carrier is required.", true, false) : string().notRequired(),
        policyNumber: isTerm || ledger === "inforceLedgerNeeded" ? string()
        .required("Policy Number is required.")
        .matches(/^[a-zA-Z0-9]+$/, 'Invalid Policy Number') : string().notRequired(),
        otherOwner: string().notRequired(),
        objective:string().notRequired().matches(/(^$)|^[^\s]+(\s+[^\s]+)*$/,'Extra spaces are not allowed.'),
        coverage: string().notRequired(),
        ownerType: string().when('otherOwner', {
            is: (otherOwner) => otherOwner === 'true',
            then: string().required('Owner Type is required.'),
            otherwise: string().notRequired()
        }),
        ownerFirstName: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'person',
            then:  customValidators.string()
            .isValidNameLimitedSpecialChar('Owner First Name is required.', true, false),
            otherwise: string().notRequired()
        }),
        ownerLastName: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'person',
            then: customValidators.string()
            .isValidNameLimitedSpecialChar("Owner Last Name is required.", true, false),
            otherwise: string().notRequired()
        }),
        ownerEmail: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'person',
            then: string().email('Email address must be valid').required('Owner Email is required.'),
            otherwise: string().notRequired()
        }),
        trustName: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'trust',
            then: customValidators.string()
            .isValidNameLimitedSpecialChar('Trust Name is required.', true, false),
            otherwise: string().notRequired()
        }),
        trusteeEmail: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'trust',
            then: string().email('Email address must be valid').required('Trustee Email is required.'),
            otherwise: string().notRequired()
        }),
        taxID: string().when(['otherOwner', 'ownerType'], {
            is: (otherOwner, ownerType) => otherOwner === 'true' && ownerType === 'trust',
            then: string()
            .required('Tax ID is required.')
            .matches(/^[a-z0-9]+$/, 'Invalid Tax ID'),
            otherwise: string().notRequired()
        }),
    }
    if (isTerm) {
        return (
            object().shape({
                planName: customValidators.string()
                    .isValidNameLimitedSpecialChar("Plan Name is required.", true, false),
                health: string().required('Health Class is required.'),
                premium: string().required('Premium is required.'),
                paymentFrequency: string().required('Payment Frequency is required.'),
                deathBenefit: string().required('Death Benefit is required.'),
                policyIssuedDate: date()
                    .required('Policy Issued Date is required.')
                    .typeError('Invalid date.'),
                tableRating: customValidators.string().isValidNameLimitedSpecialChar('', false, false),
                riders: string().notRequired().matches(/(^$)|^[^\s]+(\s+[^\s]+)*$/,'Extra spaces are not allowed.'),
                tobacco: string().required('Tobacco is required.'),
                ...common
            }))}
    else return(
        object().shape({
            beneficiaries : ledger!=="inforceLedgerNeeded"? customValidators.string().isValidNameLimitedSpecialChar("Beneficiaries is required.", true, false): string().notRequired(),
            ... common
        }))
}

ExistingPolicyInformation.DefaultFormState = (prefill = false) => {
    return {
        carrier: prefill ? 'RS' : '',
        planName: prefill ? 'S' : '',
        policyNumber: prefill ? '12345' : '',
        health: prefill ? 'Preferred Plus' : '',
        premium: prefill ? '45' : '',
        deathBenefit: prefill ? '45' : '',
        paymentFrequency: prefill ? 'A' : '',
        policyIssuedDate: prefill ? '01/01/1980' : '',
        tableRating: prefill ? '4' : '',
        riders: prefill ? 'abc' : '',
        tobacco: prefill ? 'yes' : '',
        otherOwner: prefill ? false : false,
        objective: prefill ? 'abc' : '',
        coverage: prefill ? '45' : '',
        ownerType: prefill ? '' : '',
        ownerFirstName: prefill ? '' : '',
        ownerLastName: prefill ? '' : '',
        ownerEmail: prefill ? '' : '',
        trustName: prefill ? '' : '',
        trusteeEmail: prefill ? '' : '',
        taxID: prefill ? '' : '',
        beneficiaries: prefill ? '' : '',
    }
};




// WEBPACK FOOTER //
// ./src/components/PolicyReview/ExistingPolicyInformation.js